import type {
  GridColDef,
  GridSortDirection,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';

/**
 * Grouping options for gains/losses - shared between realized and unrealized
 */
export enum GainLossGroupByMode {
  NoGroup = 'no-group',
  ByHolding = 'by-holding',
  ByAccount = 'by-account',
  ByTerm = 'by-term',
  ByGainLoss = 'by-gain-loss',
  ByAssetAllocation = 'by-asset-allocation',
}

/**
 * Returns the DataGridPremium row grouping model for a given grouping mode.
 * Empty array means no grouping.
 */
export function getGainLossGroupingModel(
  groupBy: GainLossGroupByMode,
): string[] {
  switch (groupBy) {
    case GainLossGroupByMode.ByHolding:
      return ['HoldingName'];
    case GainLossGroupByMode.ByAccount:
      return ['AccountName'];
    case GainLossGroupByMode.ByTerm:
      return ['TaxLotTermDisplay'];
    case GainLossGroupByMode.ByGainLoss:
      return ['GainTypeDisplay'];
    case GainLossGroupByMode.ByAssetAllocation:
      return ['DiversificationName'];
    default:
      return [];
  }
}

/**
 * Extra query params to send to the gains endpoints based on the active
 * grouping. Empty for modes that don't need server-side help.
 */
export function getGainLossGroupQueryParams(groupBy: GainLossGroupByMode): {
  groupValueProperty?: string;
  groupDisplayProperty?: string;
} {
  if (groupBy === GainLossGroupByMode.ByAssetAllocation) {
    return {
      groupValueProperty: 'DiversificationId',
      groupDisplayProperty: 'DiversificationName',
    };
  }
  return {};
}

/**
 * Filter params shared by the realized/unrealized portfolio-level gains
 * queries and their export requests. Built in one place so a filter added to
 * the live query (investor scope, exclusions, grouping) can't be forgotten
 * when wiring up export for a widget.
 */
export function getGainLossFilterQueryParams(
  investorId: number | undefined,
  exclusions: {
    excludedAccounts: number[];
    excludedAccountTypes: number[];
    excludedCategories: number[];
  },
  groupBy: GainLossGroupByMode,
): {
  InvestorId?: number;
  ExcludedAccounts?: string;
  ExcludedAccountTypes?: string;
  ExcludedCategories?: string;
  groupValueProperty?: string;
  groupDisplayProperty?: string;
} {
  const serializedExcludedAccounts = exclusions.excludedAccounts.join(',');
  const serializedExcludedAccountTypes =
    exclusions.excludedAccountTypes.join(',');
  const serializedExcludedCategories = exclusions.excludedCategories.join(',');

  return {
    InvestorId: investorId,
    ...(serializedExcludedAccounts
      ? { ExcludedAccounts: serializedExcludedAccounts }
      : {}),
    ...(serializedExcludedAccountTypes
      ? { ExcludedAccountTypes: serializedExcludedAccountTypes }
      : {}),
    ...(serializedExcludedCategories
      ? { ExcludedCategories: serializedExcludedCategories }
      : {}),
    ...getGainLossGroupQueryParams(groupBy),
  };
}

/**
 * Whether the grid should show the tax lot detail panel (expand control) for a row.
 * Per ticket: single-lot holdings have no expansion control; only multi-lot holdings expand.
 */
export function shouldShowTaxLotDetailPanel(
  row: { WealthHoldingId?: number; NumberOfTaxLots?: number },
  holdingId?: number,
): boolean {
  if (holdingId != null && holdingId !== 0) return false;
  if (row.WealthHoldingId == null) return false;
  return (row.NumberOfTaxLots ?? 0) > 1;
}

type DateFieldsRecord = {
  NumberOfTaxLots?: number;
  OpenDate?: string;
  TradeDate?: string;
  SaleDate?: string;
};

/**
 * Returns date field values for a holding-level row. When the holding has
 * multiple tax lots, returns undefined so the grid shows "Multiple" (ticket requirement).
 * For single-lot, uses record TradeDate/SaleDate when present (e.g. realized).
 */
export function getHoldingRowDateFields(
  record: DateFieldsRecord,
  holdingId?: number,
): {
  OpenDate: string | undefined;
  TradeDate: string | undefined;
  SaleDate: string | undefined;
} {
  const multiLot = !holdingId && (record.NumberOfTaxLots ?? 0) > 1;
  if (multiLot) {
    return { OpenDate: undefined, TradeDate: undefined, SaleDate: undefined };
  }
  return {
    OpenDate: record.OpenDate,
    TradeDate: record.TradeDate ?? record.OpenDate,
    SaleDate: record.SaleDate ?? record.OpenDate,
  };
}

export const TAX_LOT_NUMBER_FIELD = 'TaxLotNumber';

export function formatTaxLotNumber(taxLotNumber?: number | null): string {
  return taxLotNumber ? String(taxLotNumber) : '';
}

export function getTaxLotNumberSortComparator(
  direction: GridSortDirection,
): (a: string, b: string) => number {
  const sign = direction === 'desc' ? -1 : 1;
  return (a, b) => {
    if (a === b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return sign * (Number(a) - Number(b));
  };
}

export function withTaxLotNumberColumn<R extends GridValidRowModel>(
  columns: GridColDef<R>[],
  headerName: string,
): GridColDef<R>[] {
  if (columns.some((column) => column.field === TAX_LOT_NUMBER_FIELD)) {
    return columns;
  }
  return [{ field: TAX_LOT_NUMBER_FIELD, headerName }, ...columns];
}
